import clsx from "clsx";
import { memo, useMemo } from "react";
import { PieChart } from "react-minimal-pie-chart";

import { formatNumber, formatPrice } from "~/helpers/helpers.ts";

import styles from "./BillingCharts.module.scss";
import { AiEngineData, ChartData, CrawlRankingData } from "./types";

interface BillingChartsProps {
  crawlRankingData: CrawlRankingData;
  aiEngineData: AiEngineData;
}

const HEADERS = ["Feature", "QTY", "Unit Price", "Amount"] as const;

function usePieData(data: ChartData[]) {
  return useMemo(
    () =>
      data.map((item) => ({
        title: item.feature,
        value: item.quantity || 1,
        color: item.quantity ? item.color : "grey",
      })),
    [data],
  );
}

const LegendRow = memo(function LegendRow({ item }: { item: ChartData }) {
  return (
    <div className={styles.chartsLegendItem}>
      <div
        className={styles.chartsLegendColor}
        style={{ backgroundColor: item.color }}
        aria-hidden="true"
      />
      <div className={styles.chartsLegendText}>
        {/*
         // @ts-expect-error TS2339 non-existing className */}
        <span className={styles.chartsTextEllipsis} title={item.feature}>
          {item.feature}
        </span>
        <span className={styles.chartsTextRight}>
          {formatNumber(item.quantity || 0)}
        </span>
        <span className={styles.chartsTextRight}>
          {formatPrice(item.unitPrice || 0)}
        </span>
        <span className={styles.chartsTextRight}>
          {formatPrice(item.amount || 0)}
        </span>
      </div>
    </div>
  );
});

function ChartSection({
  title,
  data,
  ariaId,
}: {
  title: string;
  data: ChartData[];
  ariaId: string;
}) {
  // amount DESC
  const sorted = useMemo(
    () => [...data].sort((a, b) => (b.amount ?? 0) - (a.amount ?? 0)),
    [data],
  );
  const pieData = usePieData(sorted);

  return (
    <div className={styles.chartsCard}>
      <h2 className={styles.chartsTitle} id={ariaId}>
        {title}
      </h2>

      <div className={styles.chartsContent}>
        <PieChart
          lineWidth={15}
          data={pieData}
          aria-labelledby={ariaId}
          animate
        />

        <div className={styles.chartsLegend}>
          <div className={styles.chartsLegendHeader}>
            {HEADERS.map((header, idx) => (
              <span
                key={header}
                className={clsx({ [styles.chartsTextRight]: idx !== 0 })}
              >
                {header}
              </span>
            ))}
          </div>

          {sorted.map((item) => (
            <LegendRow
              key={`${item.feature}-${item.unitPrice}-${item.amount}`}
              item={item}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export const BillingCharts = ({
  crawlRankingData,
  aiEngineData,
}: BillingChartsProps) => {
  return (
    <div className={styles.charts}>
      {crawlRankingData.length > 0 && (
        <ChartSection
          title="Crawl ranking usage distribution"
          data={crawlRankingData}
          ariaId="crawl-ranking-usage"
        />
      )}
      {aiEngineData.length > 0 && (
        <ChartSection
          title="AI Engine usage distribution"
          data={aiEngineData}
          ariaId="ai-engine-usage"
        />
      )}
    </div>
  );
};
